import { createContext, useContext, useMemo, ReactNode } from 'react';
import { can, PermissionKey, WorkspaceRole } from '@/lib/permissions';
import { Entitlements, FeatureKey, hasFeature } from '@/lib/plans';
import { GenerationCosts } from '@/lib/creditCosts';

// What credit-spending actions cost, from the backend's own tables. Optional: absent from a
// response that predates it, in which case callers fall back to the copies in lib/creditCosts.ts.
export interface CreditCosts {
  generation?: GenerationCosts;
}

export interface Workspace {
  id: string;
  name: string;
  creditsRemaining: number;
  creditLimit: number;
  topUpCreditsRemaining?: number;
  planKey?: string | null;
  planRenewsAt?: string | null;
  subscriptionStatus?: string | null;
  cancelAtPeriodEnd?: boolean;
  description?: string | null;
  domain?: string | null;
}

const EMPTY_ENTITLEMENTS: Entitlements = { planKey: null, features: [], limits: {} };

interface WorkspaceContextValue {
  workspace: Workspace;
  workspaceId: string;
  role: WorkspaceRole;
  permissions: string[];
  isSuperAdmin: boolean;
  entitlements: Entitlements;
  creditCosts: CreditCosts;
  // Re-fetch the workspace — its balance above all — and every dashboard figure derived from it.
  // Call after anything that spends or adds credits; the balance is otherwise only as fresh as the
  // last focus or the periodic refresh in WorkspaceLayout.
  refreshWorkspace: () => void;
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

const NO_CREDIT_COSTS: CreditCosts = {};

export function WorkspaceProvider({
  workspace,
  workspaceId,
  role,
  permissions,
  isSuperAdmin,
  entitlements,
  creditCosts,
  refreshWorkspace,
  children,
}: {
  workspace: Workspace;
  workspaceId: string;
  role: WorkspaceRole;
  permissions: string[];
  isSuperAdmin: boolean;
  entitlements: Entitlements;
  creditCosts?: CreditCosts;
  refreshWorkspace: () => void;
  children: ReactNode;
}) {
  const value = useMemo(
    () => ({
      workspace,
      workspaceId,
      role,
      permissions,
      isSuperAdmin,
      entitlements: entitlements || EMPTY_ENTITLEMENTS,
      creditCosts: creditCosts || NO_CREDIT_COSTS,
      refreshWorkspace,
    }),
    [workspace, workspaceId, role, permissions, isSuperAdmin, entitlements, creditCosts, refreshWorkspace],
  );
  return <WorkspaceContext.Provider value={value}>{children}</WorkspaceContext.Provider>;
}

export function useWorkspace() {
  const ctx = useContext(WorkspaceContext);
  if (!ctx) throw new Error('useWorkspace must be used within a WorkspaceProvider');
  return ctx;
}

// Non-throwing variant for components (e.g. Navbar) that also render outside a workspace.
export function useWorkspaceOptional() {
  return useContext(WorkspaceContext);
}

export function useWorkspaceId() {
  return useWorkspace().workspaceId;
}

export function useWorkspaceRole() {
  return useWorkspace().role;
}

export function usePermissions() {
  const { permissions } = useWorkspace();
  return { permissions, can: (key: PermissionKey) => can(permissions, key) };
}

export function useEntitlements() {
  const { entitlements } = useWorkspace();
  return { entitlements, hasFeature: (key: FeatureKey) => hasFeature(entitlements, key) };
}

export function useFeature(key: FeatureKey) {
  const { entitlements } = useWorkspace();
  return hasFeature(entitlements, key);
}

// True when the workspace has an active (or trialing) subscription — i.e. it's unlocked.
// Super admins are never gated, so they always read as active.
export function useSubscriptionActive() {
  const { workspace, isSuperAdmin } = useWorkspace();
  if (isSuperAdmin) return true;
  const s = workspace.subscriptionStatus;
  return s === 'active' || s === 'trialing';
}
